import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { apiPublic } from "../../lib/api";
import useSuppressDevOverlay from "../../lib/useSuppressDevOverlay";
import { maskCep, useCepLookup } from "../../lib/cep";
import { maskCpf, isValidCpf, CPF_INVALID_MSG } from "../../lib/cpf";
import { maskPhone, isValidPhone, PHONE_INVALID_MSG } from "../../lib/phone";
import { toast } from "sonner";
import { Heart, Sparkle, CircleNotch, CheckCircle } from "@phosphor-icons/react";

// Fundo da marca (sem dependencia externa de imagens)
const HERO_BG = "linear-gradient(150deg, #141414 0%, #2a1f1e 55%, #5e3b39 100%)";

export default function AffiliateLanding() {
  useSuppressDevOverlay();
  const { slug } = useParams();
  const [info, setInfo] = useState(undefined);
  const [form, setForm] = useState({
    name: "", cpf: "", rg: "", birth_date: "", phone: "", email: "",
    cep: "", street: "", number: "", complement: "", district: "", city: "", state: "", message: "",
  });
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { cepLoading, cepError, cepSuccess, handleCepLookup } = useCepLookup(setForm);
  const cpfInvalid = form.cpf.replace(/\D/g, "").length === 11 && !isValidCpf(form.cpf);
  const phoneInvalid = form.phone.replace(/\D/g, "").length >= 10 && !isValidPhone(form.phone);

  useEffect(() => {
    (async () => {
      try { setInfo((await apiPublic.get(`/public/affiliates/${slug}`)).data); }
      catch { setInfo(null); }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!isValidCpf(form.cpf)) { toast.error(CPF_INVALID_MSG); return; }
    if (!isValidPhone(form.phone)) { toast.error(PHONE_INVALID_MSG); return; }
    setSubmitting(true);
    try {
      await apiPublic.post(`/public/affiliates/${slug}/lead`, form);
      setDone(true);
      toast.success("Enviado! Em breve voce sera contatada.");
    } catch (e) { toast.error(e.response?.data?.detail || "Nao foi possivel enviar. Tente novamente."); }
    finally { setSubmitting(false); }
  };

  if (info === undefined) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F5F0EB] p-6">
        <div className="text-sm tracking-widest uppercase text-[#5C5A57]" data-testid="affiliate-loading">Carregando...</div>
      </div>
    );
  }

  if (info === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F5F0EB] p-6" data-testid="affiliate-not-found">
        <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-10 text-center">
          <Heart size={40} weight="duotone" color="#DDA7A5" className="mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Indicacao nao encontrada ou link expirado</h1>
          <p className="text-sm text-[#5C5A57] mt-3">Confira o link com a sua revendedora ou peca um novo convite.</p>
          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mt-8">Cris Lingerie · Banco das Revendedoras</div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F5F0EB] p-6" data-testid="affiliate-done">
        <div className="bg-white border border-[#EBE5DF] max-w-md w-full p-10 text-center">
          <Sparkle size={48} weight="duotone" color="#DDA7A5" className="mx-auto mb-3" />
          <h1 className="font-display text-3xl font-bold tracking-tight">Recebido com carinho</h1>
          <p className="text-sm text-[#5C5A57] mt-3">
            <strong>{info.reseller_name}</strong> recebeu sua mensagem e vai entrar em contato pelo WhatsApp em breve.
          </p>
          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mt-8">Cris Lingerie · Banco das Revendedoras</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid grid-cols-1 md:grid-cols-2" data-testid="affiliate-landing">
      <div
        className="relative hidden md:block bg-cover bg-center text-white p-12 flex-col justify-end"
        style={{ background: HERO_BG }}
      >
        <div className="relative">
          <div className="font-display text-3xl font-bold tracking-tight">Cris Lingerie</div>
          <div className="text-[11px] tracking-[0.22em] uppercase opacity-80 mt-1">Banco das Revendedoras</div>
        </div>
        <div className="relative max-w-md mt-32">
          <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
            Indicada por uma das nossas estrelas.
          </h1>
          <p className="text-sm mt-5 opacity-90 leading-relaxed">
            Voce esta sendo atendida por <strong>{info.reseller_name}</strong>, uma de nossas revendedoras de confianca.
            {info.referred_clients > 0 && <span className="block mt-2 opacity-80">Ja sao {info.referred_clients} cliente(s) na rede dela.</span>}
          </p>
        </div>
      </div>

      <div className="bg-[#F5F0EB] flex items-center justify-center p-6 md:p-12">
        <div className="bg-white border border-[#EBE5DF] w-full max-w-md p-8">
          <div className="flex items-center gap-2 mb-2">
            <Heart size={16} weight="fill" color="#DDA7A5" />
            <span className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57]">Indicada por</span>
          </div>
          <div className="font-display text-2xl font-bold tracking-tight mb-1">{info.reseller_name}</div>
          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mb-8">Deixe seu contato para receber as novidades</div>

          <form onSubmit={submit} className="space-y-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57] border-b border-[#EBE5DF] pb-1">Dados pessoais</div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Nome completo *</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-underline" data-testid="lead-name-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CPF *</label>
                <input required value={form.cpf} onChange={(e) => setForm({ ...form, cpf: maskCpf(e.target.value) })} className="input-underline" placeholder="000.000.000-00" inputMode="numeric" maxLength={14} data-testid="lead-cpf-input" />
                {cpfInvalid && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="lead-cpf-error">{CPF_INVALID_MSG}</div>}
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">RG</label>
                <input value={form.rg} onChange={(e) => setForm({ ...form, rg: e.target.value })} className="input-underline" data-testid="lead-rg-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Data de nascimento *</label>
                <input type="date" required value={form.birth_date} onChange={(e) => setForm({ ...form, birth_date: e.target.value })} className="input-underline" data-testid="lead-birth-input" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">WhatsApp *</label>
                <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: maskPhone(e.target.value) })} className="input-underline" placeholder="(11) 99999-9999" inputMode="numeric" maxLength={16} data-testid="lead-phone-input" />
                {phoneInvalid && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="lead-phone-error">{PHONE_INVALID_MSG}</div>}
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">E-mail *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-underline" data-testid="lead-email-input" />
            </div>

            <div className="text-[10px] tracking-[0.2em] uppercase text-[#5C5A57] border-b border-[#EBE5DF] pb-1 pt-2">Endereco</div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-1">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">CEP *</label>
                <div className="relative">
                  <input
                    required
                    value={form.cep}
                    onChange={(e) => { const v = maskCep(e.target.value); setForm({ ...form, cep: v }); handleCepLookup(v); }}
                    onBlur={(e) => handleCepLookup(e.target.value)}
                    className="input-underline"
                    placeholder="00000-000"
                    inputMode="numeric"
                    maxLength={9}
                    data-testid="lead-cep-input"
                  />
                  {cepLoading && <CircleNotch size={16} className="absolute right-0 top-1 animate-spin text-[#DDA7A5]" data-testid="lead-cep-loading" />}
                </div>
                {cepLoading && <div className="text-[10px] text-[#5C5A57] mt-1" data-testid="lead-cep-searching">Buscando endereço...</div>}
                {cepSuccess && !cepLoading && <div className="text-[10px] text-[#3F6B45] mt-1 flex items-center gap-1" data-testid="lead-cep-success"><CheckCircle size={12} weight="fill" /> Endereço localizado com sucesso.</div>}
                {cepError && <div className="text-[10px] text-[#B4564F] mt-1" data-testid="lead-cep-error">{cepError}</div>}
              </div>
              <div className="sm:col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Rua *</label>
                <input required value={form.street} onChange={(e) => setForm({ ...form, street: e.target.value })} className="input-underline" data-testid="lead-street-input" />
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Numero *</label>
                <input required value={form.number} onChange={(e) => setForm({ ...form, number: e.target.value })} className="input-underline" data-testid="lead-number-input" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Complemento</label>
                <input value={form.complement} onChange={(e) => setForm({ ...form, complement: e.target.value })} className="input-underline" data-testid="lead-complement-input" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Bairro *</label>
              <input required value={form.district} onChange={(e) => setForm({ ...form, district: e.target.value })} className="input-underline" data-testid="lead-district-input" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">Cidade *</label>
                <input required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="input-underline" data-testid="lead-city-input" />
              </div>
              <div>
                <label className="block text-[10px] tracking-widest uppercase text-[#5C5A57] mb-1">UF *</label>
                <input required maxLength={2} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value.toUpperCase() })} className="input-underline" placeholder="SP" data-testid="lead-uf-input" />
              </div>
            </div>

            <button type="submit" className="brand w-full" disabled={submitting} data-testid="submit-lead-btn">
              {submitting ? "Enviando..." : "Quero ser cliente"}
            </button>
          </form>

          <div className="text-[10px] tracking-widest uppercase text-[#5C5A57] mt-8 text-center">Cris Lingerie © Banco das Revendedoras</div>
        </div>
      </div>
    </div>
  );
}
